"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeContainerClock = computeContainerClock;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const EMPTY_CLOCK = {
    limits: [],
    clockStartDate: null,
    effectiveDeadlineDate: null,
};
function addUtcDays(start, days) {
    return new Date(start.getTime() + days * MS_PER_DAY);
}
function toUtcMidnight(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}
// The date this clock is anchored to — both the reported clockStartDate and
// the base for any TIME-limit deadline. Tanks run their own cycle (emptied /
// oldest-add) independent of area placement; everything else anchors to when
// it started accumulating in its current spot.
//
// Floored to UTC midnight so every anchor-derived clock output is
// date-pure: a persisted clockStartDate/deadlineDate can never carry a
// meaningful time-of-day (import noise normalizes away on the next
// recompute), and SQL date predicates against these columns agree with the
// read-time status bucket. The satellite move-window deadline
// (area.moveDeadline) is a genuine timestamp owned by the area rollup and is
// deliberately NOT floored here — it passes through verbatim.
function resolveClockAnchorDate(container, area) {
    const anchor = resolveRawClockAnchorDate(container, area);
    return anchor === null ? null : toUtcMidnight(anchor);
}
function resolveRawClockAnchorDate(container, area) {
    if (container.isPermanent) {
        if (container.isFlowThrough) {
            return container.lastEmptiedAt ?? container.accumulationStartDate;
        }
        // No fallback here: null means no ADDs in the current cycle, so an
        // empty tank has no clock. It's on the caller's deriver to return the
        // first ADD as oldestAddAfterLastEmpty when the tank has never been
        // emptied.
        return container.oldestAddAfterLastEmpty;
    }
    if (area?.type === "CENTRAL") {
        // A container onboarded directly into central never got a move stamp —
        // its first ADD is the date it entered central, i.e. the oldest waste
        // in its central cycle.
        return container.movedToCentralDate ?? container.accumulationStartDate;
    }
    return container.accumulationStartDate;
}
function buildRuleContext(container, area, facility, anchor) {
    return {
        state: facility.state,
        generatorStatus: facility.generatorStatus,
        areaType: area.type,
        sqgOver200Miles: container.sqgOver200Miles,
        on: anchor ?? container.accumulationStartDate ?? new Date(0),
    };
}
function buildVolumeLimit(rule, limit, source) {
    return {
        limitType: "VOLUME",
        basis: `SAA ${limit.nonAcuteGal} gal / ${limit.acuteGal} gal (${limit.acuteKg} kg) acute`,
        ruleId: rule.id,
        citation: rule.citation,
        source,
        deadlineDate: null,
    };
}
function buildMoveWindowLimit(rule, limit, source, moveDeadline) {
    return {
        limitType: "TIME",
        basis: `SAA ${limit.moveWindowDays}-day move window`,
        ruleId: rule.id,
        citation: rule.citation,
        source,
        deadlineDate: moveDeadline,
    };
}
function buildTimeLimit(rule, limit, source, areaType, anchor) {
    const statusLabel = rule.generatorStatus === "ANY" ? "any generator" : rule.generatorStatus;
    return {
        limitType: "TIME",
        basis: `${limit.days} days — ${statusLabel}, ${areaType.toLowerCase()}`,
        ruleId: rule.id,
        citation: rule.citation,
        source,
        deadlineDate: addUtcDays(anchor, limit.days),
    };
}
function computeContainerClock(container, area, facility, rules) {
    if (container.status === "SHIPPED" || container.archivedAt !== null || area === null) {
        return EMPTY_CLOCK;
    }
    const anchor = resolveClockAnchorDate(container, area);
    const ctx = buildRuleContext(container, area, facility, anchor);
    const resolvedRuleset = rules(ctx);
    const limits = [];
    for (const resolved of resolvedRuleset) {
        const { rule, source } = resolved;
        const { limit } = rule;
        switch (limit.kind) {
            case "VOLUME": {
                limits.push(buildVolumeLimit(rule, limit, source));
                if (area.isOverThreshold && area.moveDeadline !== null) {
                    limits.push(buildMoveWindowLimit(rule, limit, source, area.moveDeadline));
                }
                break;
            }
            case "TIME": {
                // A resolved TIME rule already implies generatorStatus was
                // resolvable — see getAccumulationRuleset's ANY-only null matching.
                if (anchor !== null) {
                    limits.push(buildTimeLimit(rule, limit, source, ctx.areaType, anchor));
                }
                break;
            }
            default: {
                const exhaustiveCheck = limit;
                void exhaustiveCheck;
            }
        }
    }
    const timeDeadlines = limits
        .map((limit) => limit.deadlineDate)
        .filter((deadline) => deadline !== null);
    const effectiveDeadlineDate = timeDeadlines.length > 0
        ? new Date(Math.min(...timeDeadlines.map((deadline) => deadline.getTime())))
        : null;
    return {
        limits,
        clockStartDate: anchor,
        effectiveDeadlineDate,
    };
}
//# sourceMappingURL=computeContainerClock.js.map